import React from "react";
import { Button, CircularProgress } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import ErrorAlert from "components/Shared/ErrorAlert";

import {
  useCheckNCHLPaymentStatusMutation,
  useGenerateNCHLPaymentQRMutation,
} from "store/services/PaymentsApi/paymentApi";

import nchlLogo from "assets/icons/iconncc.png";

const DriverNCHLQRPayment = React.forwardRef(
  ({ amount = 0, onSuccess = () => { }, isBusy = false }, ref) => {
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [isQr, setQr] = React.useState(null);
    // Guards against firing onSuccess more than once for the same payment
    // (the "Complete Delivery" button stays enabled while paymentSuccess
    // is true, so a quick double-click submits twice). Reset whenever the
    // dialog is closed so a new payment attempt can notify again.
    const onSuccessCalledRef = React.useRef(false);

    // RTK Query. The second tuple element (the result object) carries the
    // .reset() method — the trigger function does NOT — so we have to
    // keep a reference to the whole result object, not just destructure
    // its fields. (Calling .reset() on the trigger throws
    // `checkPaymentStatus.reset is not a function`.)
    const [generateQr, generateQrResult] =
      useGenerateNCHLPaymentQRMutation();
    const { data: qrData, isLoading: loadingQR } = generateQrResult;

    const [
      checkPaymentStatus,
      checkPaymentStatusResult,
    ] = useCheckNCHLPaymentStatusMutation();
    const { data: paymentStatus, isLoading: checkingStatus } =
      checkPaymentStatusResult;

    // Single source of truth for "close the dialog and reset everything".
    // RTK Query mutations persist their last result until re-triggered or
    // explicitly reset — without .reset() a stale `paymentStatus.debitStatus
    // === "000"` from a previous payment would leave `paymentSuccess` true
    // on the next open, instantly enabling the "Complete Delivery" button
    // for a payment that hasn't happened yet (a real correctness bug — the
    // user would submit with the previous payment's data).
    const handleCloseQR = React.useCallback(() => {
      setShowConfirm(false);
      setError(null);
      onSuccessCalledRef.current = false;
      // Clear RTK state first so the next handleGenerateQR() cannot
      // observe the previous payment's debitStatus. These .reset() calls
      // are on the result objects, not on the trigger functions.
      checkPaymentStatusResult.reset();
      generateQrResult.reset();
    }, [checkPaymentStatusResult, generateQrResult]);

    // methods
    const handleGenerateQR = async () => {
      setError(null);
      try {
        setShowConfirm(true);
        if (!qrData || amount !== isQr?.amount) {
          const res = await generateQr({ amount }).unwrap();
          setQr({ validationTraceId: res?.validationTraceId, amount });
        }
      } catch (error) {
        setError(error);
      }
    };

    const handleCheckStatus = React.useCallback(async () => {
      if (loadingQR) return;

      try {
        await checkPaymentStatus({
          //   validationTraceId: "2505270031207379ALF",
          validationTraceId: qrData?.validationTraceId,
        }).unwrap();
      } catch (error) {
        setError(error);
      }
    }, [checkPaymentStatus, loadingQR, qrData?.validationTraceId]);

    const paymentSuccess = React.useMemo(
      () =>
        // Payment is successful if debitStatus equals "000"
        paymentStatus?.debitStatus === "000",
      [paymentStatus?.debitStatus]
    );

    const renderQR = React.useMemo(() => {
      if (loadingQR) return <CircularProgress size={120} />;

      if (!qrData?.qrString) return null;

      return (
        <>
          <img src={nchlLogo} alt="NCHL Logo" className="aspect-auto h-20" />

          <div className="relative flex flex-col gap-3 text-center">
            <img
              src={`https://qrcode.nepalcanmove.com/?data=${qrData?.qrString}`}
              alt="NCHL"
              className="mx-auto aspect-square w-72"
              loading="eager"
            />

            <div className=" text-sm">Scan QR to pay Rs. {amount}</div>

            {checkingStatus && (
              <div className="absolute inset-0 flex items-center justify-center rounded bg-gray-800 bg-opacity-20 backdrop-blur">
                <CircularProgress color="success" size={120} />
              </div>
            )}

            {!checkingStatus && paymentSuccess && (
              <div className="absolute -right-2 -top-2">
                <CheckCircle fontSize="large" color="success" />
              </div>
            )}

            <Button
              variant="outlined"
              className="mt-2"
              onClick={handleCheckStatus}
            >
              Check Payment
            </Button>
          </div>
        </>
      );
    }, [
      amount,
      checkingStatus,
      handleCheckStatus,
      loadingQR,
      paymentSuccess,
      qrData?.qrString,
    ]);

    React.useImperativeHandle(ref, () => ({
      handleGenerateQR,
      handleCloseQR,
    }));

    return (
      <AppConfirmDialog
        fullWidth
        hideIcon
        maxWidth="xs"
        title="Pay Via NCHL"
        open={showConfirm}
        onClose={handleCloseQR}
        onConfirm={
          paymentSuccess
            ? () => {
              // Reentrancy guard — the "Complete Delivery" button stays
              // enabled while paymentSuccess is true, so a quick
              // double-click would otherwise fire onSuccess twice and
              // submit the delivery form twice via handleSubmit().
              if (onSuccessCalledRef.current) return;
              onSuccessCalledRef.current = true;
              onSuccess(paymentStatus);
            }
            : handleCheckStatus
        }
        confirmText="Complete Delivery"
        disableConfirm={!paymentSuccess}
        loading={isBusy}
      >
        <div className="flex min-h-44 flex-col items-center gap-3">
          <ErrorAlert error={!!error ? { message: "Invalid Payment" } : null} />

          {renderQR}
        </div>
      </AppConfirmDialog>
    );
  }
);

export default DriverNCHLQRPayment;
